import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, ScatterChart, Scatter, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ZAxis
} from 'recharts';

// Import sample data
import { 
  brands, 
  productCategoriesL1, 
  productCategoriesL2, 
  productCategoriesL3,
  products 
} from '../../data/productData';

// Import custom components
import PathNavigation from '../common/PathNavigation';
import VisualizationControls from '../common/VisualizationControls';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#8DD1E1', '#A4DE6C', '#D0ED57', '#FFAA33', '#5BAEFB'];

const BrandAnalysis = () => {
  const [selectedProductCategory, setSelectedProductCategory] = useState('all');
  const [selectedSubcategory, setSelectedSubcategory] = useState('all');
  const [selectedDetailedCategory, setSelectedDetailedCategory] = useState('all');
  const [selectedMetric, setSelectedMetric] = useState('totalSales');
  const [sortBy, setSortBy] = useState('totalSales');
  const [sortDirection, setSortDirection] = useState('desc');
  const [availableSubcategories, setAvailableSubcategories] = useState([]);
  const [availableDetailedCategories, setAvailableDetailedCategories] = useState([]);
  const [currentLevel, setCurrentLevel] = useState(1); // Track hierarchy level (1=L1, 2=L2, 3=L3)
  const [pathHistory, setPathHistory] = useState([]);
  const [visualizationType, setVisualizationType] = useState('piechart'); // Default visualization type
  
  // Update available subcategories when top-level category changes
  useEffect(() => {
    if (selectedProductCategory !== 'all') {
      const categoryId = parseInt(selectedProductCategory);
      const subcats = productCategoriesL2
        .filter(cat => cat.parentId === categoryId)
        .slice(0, 5); // Limit to 5 for POC
      setAvailableSubcategories(subcats);
    } else {
      setAvailableSubcategories([]);
    }
    
    // Reset subcategory and detailed category selections
    setSelectedSubcategory('all');
    setSelectedDetailedCategory('all');
    
    // Update current level
    setCurrentLevel(selectedProductCategory === 'all' ? 1 : 2);
    
    // Update path history
    const newPath = [];
    if (selectedProductCategory !== 'all') {
      const cat = productCategoriesL1.find(c => c.id === parseInt(selectedProductCategory));
      if (cat) {
        newPath.push({ id: cat.id, name: cat.name, level: 1 });
      }
    }
    setPathHistory(newPath);
    
  }, [selectedProductCategory]);
  
  // Update available detailed categories when subcategory changes
  useEffect(() => {
    if (selectedSubcategory !== 'all') {
      const subcategoryId = parseInt(selectedSubcategory);
      const detailedCats = productCategoriesL3
        .filter(cat => cat.parentId === subcategoryId)
        .slice(0, 5); // Limit to 5 for POC
      setAvailableDetailedCategories(detailedCats);
    } else {
      setAvailableDetailedCategories([]);
    }
    
    // Reset detailed category selection
    setSelectedDetailedCategory('all');
    
    // Update current level
    setCurrentLevel(selectedSubcategory === 'all' ? 
      (selectedProductCategory === 'all' ? 1 : 2) : 3);
    
    // Update path history
    let newPath = [];
    if (selectedProductCategory !== 'all') {
      const cat = productCategoriesL1.find(c => c.id === parseInt(selectedProductCategory));
      if (cat) {
        newPath.push({ id: cat.id, name: cat.name, level: 1 });
      }
    }
    if (selectedSubcategory !== 'all') {
      const subcat = productCategoriesL2.find(c => c.id === parseInt(selectedSubcategory));
      if (subcat) {
        newPath.push({ id: subcat.id, name: subcat.name, level: 2 });
      }
    }
    setPathHistory(newPath);
    
  }, [selectedSubcategory, selectedProductCategory]);
  
  // Update level when detailed category changes
  useEffect(() => {
    // Update current level
    setCurrentLevel(selectedDetailedCategory === 'all' ? 
      (selectedSubcategory === 'all' ? 
        (selectedProductCategory === 'all' ? 1 : 2) : 3) : 4);
    
    // Update path history
    let newPath = [];
    if (selectedProductCategory !== 'all') {
      const cat = productCategoriesL1.find(c => c.id === parseInt(selectedProductCategory));
      if (cat) {
        newPath.push({ id: cat.id, name: cat.name, level: 1 });
      }
    }
    if (selectedSubcategory !== 'all') {
      const subcat = productCategoriesL2.find(c => c.id === parseInt(selectedSubcategory));
      if (subcat) {
        newPath.push({ id: subcat.id, name: subcat.name, level: 2 });
      }
    }
    if (selectedDetailedCategory !== 'all') {
      const detailedCat = productCategoriesL3.find(c => c.id === parseInt(selectedDetailedCategory));
      if (detailedCat) {
        newPath.push({ id: detailedCat.id, name: detailedCat.name, level: 3 });
      }
    }
    setPathHistory(newPath);
    
  }, [selectedDetailedCategory, selectedSubcategory, selectedProductCategory]);
  
  // Filter brands based on selected product category hierarchy
  const getFilteredBrands = () => {
    if (selectedDetailedCategory !== 'all') {
      // Filter by L3 category - find brands with products in this category
      const detailCatId = parseInt(selectedDetailedCategory);
      const productsInCategory = products.filter(p => p.categoryL3Id === detailCatId);
      const brandIdsInCategory = [...new Set(productsInCategory.map(p => p.brandId))];
      return brands.filter(brand => brandIdsInCategory.includes(brand.id));
    }
    else if (selectedSubcategory !== 'all') {
      // Filter by L2 category
      const subcatId = parseInt(selectedSubcategory);
      const l3CatsForL2 = productCategoriesL3.filter(l3 => l3.parentId === subcatId);
      const l3CatIds = l3CatsForL2.map(l3 => l3.id);
      
      const productsInCategories = products.filter(p => l3CatIds.includes(p.categoryL3Id));
      const brandIdsInCategories = [...new Set(productsInCategories.map(p => p.brandId))];
      return brands.filter(brand => brandIdsInCategories.includes(brand.id));
    }
    else if (selectedProductCategory !== 'all') {
      // Filter by L1 category
      const categoryId = parseInt(selectedProductCategory);
      return brands.filter(brand => brand.mainCategories.includes(categoryId));
    } 
    else {
      // No filter - return all brands
      return brands;
    }
  };
  
  // Sort brands based on selected criteria
  const getSortedBrands = () => {
    const filtered = getFilteredBrands();
    
    return [...filtered].sort((a, b) => {
      const valueA = a[sortBy];
      const valueB = b[sortBy];
      
      if (sortDirection === 'asc') {
        return valueA - valueB;
      } else {
        return valueB - valueA;
      }
    });
  };
  
  // Toggle sort direction
  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('desc');
    }
  };
  
  // Prepare data for scatter plot
  const prepareScatterData = () => {
    return getFilteredBrands().map(brand => {
      // Get category names based on current level
      let categoryNames = [];
      
      if (selectedDetailedCategory !== 'all') {
        const category = productCategoriesL3.find(c => c.id === parseInt(selectedDetailedCategory));
        categoryNames = [category?.name || 'Unknown'];
      } 
      else if (selectedSubcategory !== 'all') {
        const category = productCategoriesL2.find(c => c.id === parseInt(selectedSubcategory));
        categoryNames = [category?.name || 'Unknown'];
      }
      else if (selectedProductCategory !== 'all') {
        const category = productCategoriesL1.find(c => c.id === parseInt(selectedProductCategory));
        categoryNames = [category?.name || 'Unknown'];
      }
      else {
        // Show main categories for brand
        categoryNames = brand.mainCategories.map(catId => {
          const category = productCategoriesL1.find(c => c.id === catId);
          return category ? category.name : `Category ${catId}`;
        });
      }
      
      return {
        id: brand.id,
        name: brand.name,
        x: brand.totalSales / 1000000, // Convert to millions for better display
        y: brand.growth,
        z: brand.productCount / 100, // Normalize size for better visual
        categories: categoryNames.join(', '),
        categoryCount: categoryNames.length
      };
    });
  };
  
  // Format metric values for display
  const formatMetricValue = (value, metric) => {
    switch(metric) {
      case 'totalSales':
        return `$${(value/1000000).toFixed(2)}M`;
      case 'growth':
        return `${value.toFixed(1)}%`;
      default:
        return value.toLocaleString();
    }
  };
  
  // Custom tooltip for scatter chart
  const renderScatterTooltip = (props) => {
    const { active, payload } = props;
    
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      
      return (
        <div className="bg-white p-3 border border-gray-200 shadow-sm rounded-md">
          <p className="font-semibold">{data.name}</p>
          <p className="text-sm">Sales: ${data.x.toFixed(2)}M</p>
          <p className="text-sm">Growth: {data.y.toFixed(1)}%</p>
          <p className="text-sm">Products: {data.z * 100}</p>
          <p className="text-sm">Categories: {data.categories}</p>
        </div>
      );
    }
    
    return null;
  };
  
  // Prepare data for charts
  const prepareChartData = () => {
    const sortedBrands = getSortedBrands().slice(0, 10);
    
    return sortedBrands.map(brand => ({
      name: brand.name,
      value: brand[selectedMetric]
    }));
  };
  
  // Prepare data for line chart
  const prepareLineChartData = () => {
    const filteredBrands = getSortedBrands().slice(0, 5); // Limit to top 5 brands
    
    // For demo purposes, create monthly data
    const timeSeriesData = [];
    
    // Generate 12 months of data for each brand
    for (let month = 1; month <= 12; month++) {
      const monthData = {
        month: `Month ${month}`,
      };
      
      // Add data for each brand
      filteredBrands.forEach(brand => {
        // Base value from the selected metric
        let baseValue = brand[selectedMetric] || 0;
        
        // Create a seasonal pattern with some randomness
        const seasonalFactor = 1 + 0.2 * Math.sin(month / 12 * 2 * Math.PI);
        const randomFactor = 0.95 + Math.random() * 0.1; // Random 0.95-1.05
        
        // Calculate monthly value
        const value = baseValue * seasonalFactor * randomFactor / 12;
        
        monthData[brand.name] = value;
      });
      
      timeSeriesData.push(monthData);
    }
    
    return { data: timeSeriesData, brands: filteredBrands };
  };
  
  // Navigate up to a specific level
  const navigateUp = (targetLevel) => {
    switch(targetLevel) {
      case 1:
        // Reset to top level
        setSelectedProductCategory('all');
        setSelectedSubcategory('all');
        setSelectedDetailedCategory('all');
        break;
      case 2:
        // Keep L1 selection, reset L2 and L3
        setSelectedSubcategory('all');
        setSelectedDetailedCategory('all');
        break;
      case 3:
        // Keep L1 and L2 selections, reset L3
        setSelectedDetailedCategory('all');
        break;
      default:
        // Default to top level
        setSelectedProductCategory('all');
        setSelectedSubcategory('all');
        setSelectedDetailedCategory('all');
    }
  };
  
  // Get title based on current hierarchy level
  const getCurrentTitle = () => {
    if (selectedDetailedCategory !== 'all') {
      const detailCat = productCategoriesL3.find(c => c.id === parseInt(selectedDetailedCategory));
      return `Brands in ${detailCat?.name || 'Selected Category'}`;
    }
    else if (selectedSubcategory !== 'all') {
      const subcat = productCategoriesL2.find(c => c.id === parseInt(selectedSubcategory));
      return `Brands in ${subcat?.name || 'Selected Subcategory'}`;
    }
    else if (selectedProductCategory !== 'all') {
      const cat = productCategoriesL1.find(c => c.id === parseInt(selectedProductCategory));
      return `Brands in ${cat?.name || 'Selected Category'}`;
    }
    else {
      return 'All Brands';
    }
  };
  
  // Navigate to breadcrumb
  const navigateToBreadcrumb = (item) => {
    switch(item.level) {
      case 1:
        // Navigate to L1
        setSelectedProductCategory(item.id.toString());
        setSelectedSubcategory('all');
        setSelectedDetailedCategory('all');
        break;
      case 2:
        // Navigate to L2
        setSelectedSubcategory(item.id.toString());
        setSelectedDetailedCategory('all');
        break;
      case 3:
        // Navigate to L3
        setSelectedDetailedCategory(item.id.toString());
        break;
    }
  };
  
  // Render pie chart
  const renderPieChart = () => {
    const data = prepareChartData();
    
    if (!data || data.length === 0) {
      return <div className="text-center py-12 text-gray-500">No data available for the selected categories</div>;
    }
    
    return (
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={true}
            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
            outerRadius={120}
            fill="#8884d8"
            dataKey="value"
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(value) => formatMetricValue(value, selectedMetric)} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    );
  };
  
  // Render bar chart
  const renderBarChart = () => {
    const data = prepareChartData();
    
    if (!data || data.length === 0) {
      return <div className="text-center py-12 text-gray-500">No data available for the selected categories</div>;
    }
    
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{ top: 20, right: 30, left: 40, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" angle={-45} textAnchor="end" height={70} />
          <YAxis />
          <Tooltip formatter={(value) => formatMetricValue(value, selectedMetric)} />
          <Legend />
          <Bar 
            dataKey="value" 
            fill="#0088FE" 
            name={
              selectedMetric === 'totalSales' 
                ? 'Total Sales' 
                : selectedMetric === 'growth' 
                  ? 'Growth Rate' 
                  : 'Product Count'
            }
          />
        </BarChart>
      </ResponsiveContainer>
    );
  };
  
  // Render line chart
  const renderLineChart = () => {
    const { data, brands } = prepareLineChartData();
    
    if (!data || data.length === 0 || !brands || brands.length === 0) {
      return <div className="text-center py-12 text-gray-500">No data available for the selected categories</div>;
    }
    
    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{ top: 20, right: 30, left: 40, bottom: 10 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis />
          <Tooltip formatter={(value) => formatMetricValue(value, selectedMetric)} />
          <Legend />
          {brands.map((brand, index) => (
            <Line 
              key={brand.id}
              type="monotone" 
              dataKey={brand.name} 
              stroke={COLORS[index % COLORS.length]} 
              activeDot={{ r: 8 }}
              strokeWidth={2}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  };
  
  // Render visualization based on selected type
  const renderVisualization = () => {
    switch(visualizationType) {
      case 'piechart':
        return renderPieChart();
      case 'barchart':
        return renderBarChart();
      case 'linechart':
        return renderLineChart();
      default:
        return renderPieChart();
    }
  };
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Brand Analysis</h1>
        <p className="text-gray-600 mb-4">
          Analyze brand performance across different product categories. Compare sales, growth, and product distribution.
        </p>
        
        {/* Enhanced Navigation breadcrumbs - Now using the PathNavigation component */}
        <PathNavigation 
          pathHistory={pathHistory}
          navigateUp={navigateUp}
          navigateToBreadcrumb={navigateToBreadcrumb}
          currentLevel={currentLevel}
        />
        
        {/* Hierarchy navigation selectors */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-3">Product Category</label>
            <select 
              value={selectedProductCategory}
              onChange={(e) => setSelectedProductCategory(e.target.value)}
              className="dropdown-selector"
            >
              <option value="all">All Categories</option>
              {productCategoriesL1.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </div>
          
          {selectedProductCategory !== 'all' && (
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-3">Subcategory</label>
              <select 
                value={selectedSubcategory}
                onChange={(e) => setSelectedSubcategory(e.target.value)}
                className="dropdown-selector"
              >
                <option value="all">All Subcategories</option>
                {availableSubcategories.map(subcat => (
                  <option key={subcat.id} value={subcat.id}>{subcat.name}</option>
                ))}
              </select>
            </div>
          )}
          
          {selectedSubcategory !== 'all' && (
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-3">Detailed Category</label>
              <select 
                value={selectedDetailedCategory}
                onChange={(e) => setSelectedDetailedCategory(e.target.value)}
                className="dropdown-selector"
              >
                <option value="all">All Detailed Categories</option>
                {availableDetailedCategories.map(detailCat => (
                  <option key={detailCat.id} value={detailCat.id}>{detailCat.name}</option>
                ))}
              </select>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-3">Performance Metric</label>
            <select 
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              className="dropdown-selector"
            >
              <option value="totalSales">Total Sales (USD)</option>
              <option value="growth">Growth Rate (%)</option>
              <option value="productCount">Product Count</option>
            </select>
          </div>
          
          {currentLevel > 1 && (
            <div className="flex items-end">
              <button
                onClick={() => navigateUp(currentLevel - 1)}
                className="px-4 py-2 rounded-md font-medium text-sm bg-blue-600 text-white hover:bg-blue-700"
              >
                ← Back Up One Level
              </button>
            </div>
          )}
        </div>
        
        {/* Level indicator */}
        <div className="bg-blue-50 p-3 rounded-md mb-6">
          <p className="text-blue-800 font-medium">
            Current View: <span className="font-bold">{getCurrentTitle()}</span>
            <span className="text-blue-600 ml-2 text-sm">
              (Showing {getFilteredBrands().length} brands)
            </span>
          </p>
        </div>
      </div>
      
      {/* Brand Performance Distribution */}
      <div className="chart-container mb-6">
        <h2 className="chart-title">Brand Performance Distribution</h2>
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart
              margin={{ top: 20, right: 20, bottom: 10, left: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                type="number" 
                dataKey="x" 
                name="Sales" 
                unit="M" 
                label={{ 
                  value: 'Sales (Millions USD)', 
                  position: 'bottom',
                  style: { textAnchor: 'middle' }
                }} 
              />
              <YAxis 
                type="number" 
                dataKey="y" 
                name="Growth" 
                unit="%" 
                label={{ 
                  value: 'Growth Rate (%)', 
                  angle: -90, 
                  position: 'left',
                  style: { textAnchor: 'middle' }
                }} 
              />
              <ZAxis 
                type="number" 
                dataKey="z" 
                range={[20, 500]} 
                name="Products" 
              />
              <Tooltip content={renderScatterTooltip} cursor={{ strokeDasharray: '3 3' }} />
              <Legend />
              <Scatter name="Brands" data={prepareScatterData()}>
                {prepareScatterData().map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={COLORS[index % COLORS.length]} 
                  />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Visualization controls and charts */}
      <div className="chart-container mb-6">
        <div className="flex justify-between items-center mb-3">
          <h2 className="chart-title">
            Top 10 Brands by {selectedMetric === 'totalSales' 
                           ? 'Sales' 
                           : selectedMetric === 'growth'
                           ? 'Growth Rate'
                           : 'Product Count'}
          </h2>
          <VisualizationControls 
            visualizationType={visualizationType}
            setVisualizationType={setVisualizationType}
          />
        </div>
        <div className="h-80">
          {renderVisualization()}
        </div>
      </div>
      
      {/* Brand Performance Table */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-3">Brand Performance Details</h2>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Brand Name</th>
                <th>
                  <button 
                    onClick={() => toggleSort('totalSales')}
                    className="flex items-center"
                  >
                    Total Sales
                    {sortBy === 'totalSales' && (
                      <span className="ml-1">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </button>
                </th>
                <th>
                  <button 
                    onClick={() => toggleSort('growth')}
                    className="flex items-center"
                  >
                    Growth
                    {sortBy === 'growth' && (
                      <span className="ml-1">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </button>
                </th>
                <th>
                  <button 
                    onClick={() => toggleSort('productCount')}
                    className="flex items-center"
                  >
                    Product Count
                    {sortBy === 'productCount' && (
                      <span className="ml-1">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </button>
                </th>
                <th>Categories</th>
              </tr>
            </thead>
            <tbody>
              {getSortedBrands().map(brand => {
                // Get formatted category names based on current level
                let categoryNames;
                
                if (selectedDetailedCategory !== 'all') {
                  const category = productCategoriesL3.find(c => c.id === parseInt(selectedDetailedCategory));
                  categoryNames = category ? category.name : 'Unknown';
                } 
                else if (selectedSubcategory !== 'all') {
                  const category = productCategoriesL2.find(c => c.id === parseInt(selectedSubcategory));
                  categoryNames = category ? category.name : 'Unknown';
                }
                else if (selectedProductCategory !== 'all') {
                  const category = productCategoriesL1.find(c => c.id === parseInt(selectedProductCategory));
                  categoryNames = category ? category.name : 'Unknown';
                }
                else {
                  // Show main categories for brand
                  categoryNames = brand.mainCategories.map(catId => {
                    const category = productCategoriesL1.find(c => c.id === catId);
                    return category ? category.name : `Category ${catId}`;
                  }).join(', ');
                }
                
                return (
                  <tr key={brand.id}>
                    <td className="font-medium">{brand.name}</td>
                    <td>${(brand.totalSales / 1000000).toFixed(2)}M</td>
                    <td className={brand.growth >= 0 ? 'positive-growth' : 'negative-growth'}>
                      {brand.growth >= 0 ? '↑' : '↓'} {Math.abs(brand.growth).toFixed(1)}%
                    </td>
                    <td>{brand.productCount}</td>
                    <td>{categoryNames}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BrandAnalysis;